import React, { useState } from 'react';
import { MessageCircle, X, Send, CheckCircle } from 'lucide-react';
import useSettingsStore from '../store/useSettingsStore';
import api from '../lib/api';

const messengerOptions = ['Telegram', 'WhatsApp', 'MAX', 'Звонок', 'Другое'];

export default function FeedbackButton() {
  const { settings, siteContent, feedbackOpen, setFeedbackOpen } = useSettingsStore();
  const open = feedbackOpen;
  const setOpen = setFeedbackOpen;
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', messenger: 'Telegram', messenger_other: '', message: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const fb = siteContent?.feedback || {};
  const placeholders = siteContent?.placeholders || {};
  const tgChannel = settings?.telegram_channel_url || '#';
  const waUrl = settings?.contacts_info?.[0]?.whatsapp || '';

  const formatPhone = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 11);
    if (digits.length === 0) return '';
    let result = '+7';
    if (digits.length > 1) result += ` (${digits.slice(1, 4)}`;
    if (digits.length > 4) result += `) ${digits.slice(4, 7)}`;
    if (digits.length > 7) result += `-${digits.slice(7, 9)}`;
    if (digits.length > 9) result += `-${digits.slice(9, 11)}`;
    return result;
  };

  const validateName = (name) => {
    if (!name || name.trim().length < 2) return 'Введите настоящее имя';
    if (!/^[А-ЯЁа-яё][А-ЯЁа-яё\s\-]{0,49}$/.test(name.trim())) return 'Только русские буквы';
    return '';
  };

  const validatePhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 11) return 'Введите полный номер';
    if (new Set(digits.slice(1)).size === 1) return 'Некорректный номер';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nameErr = validateName(form.name);
    const phoneErr = validatePhone(form.phone);
    if (nameErr || phoneErr) {
      setErrors({ name: nameErr, phone: phoneErr });
      return;
    }
    setLoading(true);
    try {
      await api.post('/feedback', {
        name: form.name.trim(),
        phone: form.phone,
        messenger: form.messenger,
        messenger_other: form.messenger === 'Другое' ? form.messenger_other : undefined,
        message: form.message
      });
      setSuccess(true);
      setTimeout(() => { setOpen(false); setSuccess(false); setForm({ name: '', phone: '', messenger: 'Telegram', messenger_other: '', message: '' }); }, 3000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setErrors({ submit: detail });
      else if (Array.isArray(detail)) setErrors({ submit: detail.map(d => d.msg).join(', ') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-[99999] rounded-full w-14 h-14 flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110"
        style={{ background: 'var(--color-accent-gold)' }}
        data-testid="feedback-floating-btn"
      >
        {open ? <X size={24} style={{ color: 'var(--color-primary-dark)' }} /> : <MessageCircle size={24} style={{ color: 'var(--color-primary-dark)' }} />}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-[9998] w-[340px] max-w-[calc(100vw-48px)] rounded-xl overflow-hidden shadow-2xl md:w-[360px]"
          style={{
            background: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
            borderTop: '2px solid var(--color-accent-gold)',
            backdropFilter: 'blur(16px)',
          }}
          data-testid="feedback-panel"
        >
          {success ? (
            <div className="p-6 text-center">
              <CheckCircle size={48} style={{ color: 'var(--color-accent-green)', margin: '0 auto 16px' }} />
              <p style={{ color: 'var(--color-text-primary)' }}>{fb.success_text || 'Спасибо! Мы свяжемся с вами'}</p>
            </div>
          ) : (
            <div className="p-5">
              <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-accent-gold)' }}>
                {fb.contacts_title || 'Написать менеджеру'}
              </h3>

              {/* Quick links */}
              <div className="flex gap-3 mb-4">
                {tgChannel && tgChannel !== '#' && (
                  <a href={tgChannel} target="_blank" rel="noopener noreferrer" className="btn-gold text-xs py-2 px-3 flex-1 text-center" data-testid="feedback-tg-btn">
                    Telegram канал
                  </a>
                )}
                {waUrl && (
                  <a href={waUrl} target="_blank" rel="noopener noreferrer" className="btn-gold text-xs py-2 px-3 flex-1 text-center" data-testid="feedback-wa-btn">
                    WhatsApp
                  </a>
                )}
              </div>

              <div className="text-center text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
                {fb.separator || 'или'}
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <input
                    className={`input-premium text-sm ${errors.name ? 'error' : form.name && !validateName(form.name) ? 'success' : ''}`}
                    placeholder={placeholders.name || 'Ваше имя'}
                    value={form.name}
                    onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setErrors(er => ({ ...er, name: '' })); }}
                    data-testid="feedback-name-input"
                  />
                  {errors.name && <p className="text-xs mt-1" style={{ color: 'var(--color-accent-red-light)' }}>{errors.name}</p>}
                </div>
                <div>
                  <input
                    type="tel"
                    className={`input-premium text-sm ${errors.phone ? 'error' : form.phone && !validatePhone(form.phone) ? 'success' : ''}`}
                    placeholder={placeholders.phone || '+7 (___) ___-__-__'}
                    value={form.phone}
                    onChange={e => { setForm(f => ({ ...f, phone: formatPhone(e.target.value) })); setErrors(er => ({ ...er, phone: '' })); }}
                    data-testid="feedback-phone-input"
                  />
                  {errors.phone && <p className="text-xs mt-1" style={{ color: 'var(--color-accent-red-light)' }}>{errors.phone}</p>}
                </div>
                <select
                  className="input-premium text-sm"
                  value={form.messenger}
                  onChange={e => setForm(f => ({ ...f, messenger: e.target.value }))}
                  data-testid="feedback-messenger-select"
                >
                  {messengerOptions.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                {form.messenger === 'Другое' && (
                  <input className="input-premium text-sm" placeholder="Укажите мессенджер" value={form.messenger_other} onChange={e => setForm(f => ({ ...f, messenger_other: e.target.value }))} />
                )}
                <textarea
                  className="input-premium text-sm"
                  rows={3}
                  placeholder={placeholders.message || 'Ваше сообщение'}
                  value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  data-testid="feedback-message-input"
                />
                {errors.submit && <p className="text-xs" style={{ color: 'var(--color-accent-red-light)' }}>{errors.submit}</p>}
                <button
                  type="submit"
                  disabled={loading || !form.name || !form.phone || !!validateName(form.name) || !!validatePhone(form.phone)}
                  className="btn-gold w-full text-sm"
                  data-testid="feedback-submit-btn"
                >
                  <Send size={16} />
                  {fb.submit_button || 'Отправить заявку'}
                </button>
              </form>
            </div>
          )}
        </div>
      )}
    </>
  );
}
